<template src="./comments-pinboard-teaser.html"></template>

<script lang="ts">
import { nextTick } from 'vue'
import { debounce } from 'util/ts-utils'
import { callTurnToConfigFn, encodeArray } from 'util/turnto-utils'
import getCatalogItemsForCommentsPinboardService
  from 'services/api/get-catalog-items-for-comments-pinboard'
import pinboardTeaserStore from 'store/comments-pinboard-teaser-store'
import teaserConfig from './comments-pinboard-teaser.config'
import PinboardItem from 'components/pinboard/CommentsPinboardItem.vue'
import trackingService from 'services/tracking-service'


const MIN_ITEMS_REQUIRED = 4

export default {
  data() {
    return {
      containerWidth: 0,
      currentBreakpointConfig: {},
      items: [],
      numberOfColumns: 0,
      pinboardTeaserStore
    }
  },
  components: { 'tt-pinboard-item': PinboardItem },
  computed: {
    containerStyles() {
      return {
        'flex-wrap': this.showCommentsPromoBox && ! this.isPomoBoxAColumn ? 'wrap' : 'nowrap'
      }
    },
    currentFilters() {
      return {
        skus: this.pinboardTeaserStore.skus,
        tags: this.pinboardTeaserStore.tags,
        brands: this.pinboardTeaserStore.brands
      }
    },
    isPomoBoxAColumn() {
      return this.showCommentsPromoBox &&
        (this.promoPlacement === 'left' || this.promoPlacement === 'right')
    },
    isPromoBoxTopOrBottom() {
      return this.showCommentsPromoBox &&
        (this.promoPlacement === 'top' || this.promoPlacement === 'bottom')
    },
    itemStyles() {
      return {
        width: `${this.itemWidthPercentage}%`
      }
    },
    numberOfPlaceholders() {
      return this.numberOfColumns - this.visibleItems.length - (this.isPomoBoxAColumn ? 1 : 0)
    },
    promoPlacement() {
      return this.currentBreakpointConfig.promoPlacement
    },
    promoClasses() {
      return `tt-w-comments-pinboard-teaser__promo--${this.promoPlacement}`
    },
    promoStyles() {
      // top or bottom
      if (this.isPromoBoxTopOrBottom) {
        return {
          flex: '1 0 100%',
          order: this.promoPlacement === 'top' ? 0 : this.numberOfColumns + 1
        }
      }
      // left or right
      return {
        width: `${this.itemWidthPercentage}%`,
        order: this.promoPlacement === 'left' ? 0 : this.numberOfColumns + 1
      }
    },
    showCommentsPromoBox() {
      return teaserConfig.promoButtonUrl ? true : false
    },
    showPinboard() {
      return this.items.length >= MIN_ITEMS_REQUIRED
    },
    itemWidthPercentage() {
      // calculating percentage of each item to take the spacing into consideration.
      // the justify-content: space-between; set on the container will fill in the spacing
      // automatically.  Idea taken from one of the examples in this codepen:
      // https://codepen.io/imohkay/pen/gpard
      const spacingPercentage =
        (this.numberOfColumns - 1) * this.currentBreakpointConfig.spacing / this.containerWidth
      return Math.floor((1.0 - spacingPercentage) / this.numberOfColumns * 1000) / 10
    },
    teaserConfig() {
      return teaserConfig
    },
    visibleItems() {
      const promoAdjustment =
        this.isPomoBoxAColumn && (this.numberOfColumns - this.items.length) <= 0 ? 1 : 0
      return this.items.slice(0, this.numberOfColumns - promoAdjustment)
    }
  },
  methods: {
    calculateNumberOfColumns() {
      this.containerWidth = this.$el.clientWidth
      // sorting from highest to lowest, so the correct one is used
      const breakpoints = teaserConfig.breakpointsConfig.sort((a, b) => b.viewport - a.viewport)
      for (let i = 0; i < breakpoints.length; i++) {
        const breakpoint = breakpoints[i]
        if (breakpoint.viewport === 0) { // should be last breakpoint
          this.currentBreakpointConfig = breakpoint
          this.numberOfColumns = 2
        } else if (this.containerWidth >= breakpoint.viewport) {
          this.currentBreakpointConfig = breakpoint
          const minColWidth =
            this.currentBreakpointConfig.minWidth + this.currentBreakpointConfig.spacing
          this.numberOfColumns = Math.floor(this.containerWidth / minColWidth)
          break
        }
      }
    },
    fetchData() {
      trackingService.queueEvent('comments-pinboard-teaser', 'load')
      getCatalogItemsForCommentsPinboardService.getCatalogItemsForCommentsPinboard({
        offset: 0,
        limit: 10,
        activeOnly: teaserConfig.activeOnly,
        brands: encodeArray(pinboardTeaserStore.brands),
        skus: encodeArray(pinboardTeaserStore.skus),
        tags: encodeArray(pinboardTeaserStore.tags),
        includeCommentsCount: true,
        includeReviewData: teaserConfig.showReviewStars,
        maxDaysOld: teaserConfig.maxDaysOld,
        includeSyndicated: teaserConfig.includeSyndicated,
        minComments: 4,
        maxCommentsReturned: 2
      }).then((data) => {
        this.items = data.catalogItems
        nextTick(() => {
          this.calculateNumberOfColumns()
        })
        // waiting for pinboard to finish loading before attmepting to call onFinish
        nextTick(() => { callTurnToConfigFn('commentsPinboardTeaser', 'onFinish') })
      }).catch(() => {})
    },
    onResize: debounce(function cb() {
      this.calculateNumberOfColumns()
    }, 50)
  },
  created() {
    this.fetchData()
  },
  watch: {
    currentFilters: {
      handler() {
        this.fetchData()
      },
      deep: true
    }
  },
  mounted() {
    // remove event first so we don't accidentally add it twice
    window.removeEventListener('resize', this.onResize, false)
    window.addEventListener('resize', this.onResize, false)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize, false)
  }
}
</script>

<style lang="scss" src="./comments-pinboard-teaser.scss"></style>
